import { Button, Classes, Popover, Tab, Tabs } from "@blueprintjs/core"
import { useEffect, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { capitalizeFirstLetter } from "../../../utils"
import { useTradingDataUpdated } from "../../../websocket/hooks/useEvents"
import Factsheet from "./Factsheet"
import AccountBookFilterPanel from "./Portfolio/components/AccountBookFilter/AccountBookFilterPanel"
import useBooksQuery from "./Portfolio/hooks/useBooksQuery"
import Portfolio from "./Portfolio/Portfolio"
import { PortfolioManagementContainer } from "./PortfolioManagement.styled"
import Risks from "./Risks/Risks"
import Trading from "./Trading/Trading"
import { IAccountBookPairs, PortfolioDisplayTab } from "./types"

const PortfolioManagement = () => {
  const [selectedTab, setSelectedTab] = useState(PortfolioDisplayTab.POSITIONS)
  // Selected accounts/books
  const [selectedFilters, setSelectedFilters] = useState<IAccountBookPairs>([])
  const { defaultAccountBookPairs, isSuccess: isBooksSuccess } = useBooksQuery({})

  /**
   * Set default account/book filters from defaultAccountBookPairs if none specified yet
   */
  useEffect(() => {
    if (defaultAccountBookPairs?.length > 0 && selectedFilters.length === 0) {
      setSelectedFilters(defaultAccountBookPairs)
    }
  }, [defaultAccountBookPairs])

  const queryClient = useQueryClient()
  /**
   * Handles Portfolio Summary Live event
   */
  useTradingDataUpdated(() => {
    queryClient.invalidateQueries({ queryKey: ["tradingsummaryscient"] })
    queryClient.invalidateQueries({ queryKey: ["tradingdatascient"] })
    queryClient.invalidateQueries({ queryKey: ["riskExposures"] })
    queryClient.invalidateQueries({ queryKey: ["riskZoom"] })
  })

  return (
    <PortfolioManagementContainer flexDirection="column" selectedTab={selectedTab}>
      <Tabs
        id="pm-tabs"
        onChange={id => setSelectedTab(id as PortfolioDisplayTab)}
        selectedTabId={selectedTab}
        /**
         * renderActiveTabPanelOnly avoids warning:
         *      " AG Grid tried to call sizeColumnsToFit() but the grid is coming back
         *      with zero width, maybe the grid is not visible yet on the screen? "
         */
        renderActiveTabPanelOnly
        large
        animate={false} // prevents the indicator of the selected tab from being misplaced
      >
        <Popover
          position="bottom"
          popoverClassName={Classes.POPOVER_CONTENT_SIZING}
          content={
            <AccountBookFilterPanel
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
            />
          }
        >
          <Button icon="filter" minimal small aria-label="Open filters" />
        </Popover>
        <Tab
          id={PortfolioDisplayTab.POSITIONS}
          title={capitalizeFirstLetter(PortfolioDisplayTab.POSITIONS)}
          panel={
            <Portfolio
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              displayTab={PortfolioDisplayTab.POSITIONS}
              enableTradingQuery={isBooksSuccess}
            />
          }
        />
        <Tab
          id={PortfolioDisplayTab.ORDERS}
          title={capitalizeFirstLetter(PortfolioDisplayTab.ORDERS)}
          panel={
            <Portfolio
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              displayTab={PortfolioDisplayTab.ORDERS}
              enableTradingQuery={isBooksSuccess}
            />
          }
        />
        <Tab
          id={PortfolioDisplayTab.RISKS}
          title={capitalizeFirstLetter(PortfolioDisplayTab.RISKS)}
          panel={<Risks selectedFilters={selectedFilters} enableTradingQuery={isBooksSuccess} />}
        />
        <Tab
          id={PortfolioDisplayTab.TRADING}
          title={capitalizeFirstLetter(PortfolioDisplayTab.TRADING)}
          panel={<Trading enableTradingQuery={isBooksSuccess} selectedFilters={selectedFilters} />}
        />
        <Tab
          id={PortfolioDisplayTab.FACTSHEET}
          title={capitalizeFirstLetter(PortfolioDisplayTab.FACTSHEET)}
          panel={<Factsheet selectedFilters={selectedFilters} enableTradingQuery={isBooksSuccess} />}
        />
      </Tabs>
    </PortfolioManagementContainer>
  )
}

export default PortfolioManagement
